import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ReviewModal from "../components/ReviewModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Video, User, ExternalLink, FileText, Star } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const MyConsultations = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API}/consultations/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultations(response.data.consultations);
    } catch (error) {
      toast.error("Failed to load consultations");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "confirmed": return "bg-green-500";
      case "pending": return "bg-yellow-500";
      case "completed": return "bg-blue-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case "paid": return "bg-green-500";
      case "pending": return "bg-yellow-500";
      case "failed": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">My Consultations</h1>
            <p className="text-gray-600">View and manage your career consultation bookings</p>
          </div>
          <Button onClick={() => navigate("/consultation/book")}>
            <Video className="w-4 h-4 mr-2" />
            Book New Consultation
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading consultations...</p>
          </div>
        ) : consultations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Video className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No Consultations Yet</h3>
              <p className="text-gray-600 mb-4">Book your first consultation with our career counselors</p>
              <Button onClick={() => navigate("/consultation/book")}>
                Book Consultation
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {consultations.map((consultation) => (
              <Card key={consultation.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{consultation.topic}</CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getStatusColor(consultation.status)}>
                          {consultation.status.toUpperCase()}
                        </Badge>
                        <Badge className={getPaymentStatusColor(consultation.payment_status)}>
                          {consultation.payment_status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">${consultation.amount}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>
                        <strong>Date:</strong> {new Date(consultation.scheduled_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>
                        <strong>Time:</strong> {consultation.scheduled_time}
                      </span>
                    </div>
                    {consultation.counselor_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>
                          <strong>Counselor:</strong> {consultation.counselor_name}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span>
                        <strong>Booking ID:</strong> {consultation.id.substring(0, 8)}
                      </span>
                    </div>
                  </div>

                  {consultation.notes && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-4">
                      <p className="text-sm text-gray-700">
                        <strong>Notes:</strong> {consultation.notes}
                      </p>
                    </div>
                  )}

                  {consultation.meeting_link && consultation.status === "confirmed" && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-800 mb-2 font-medium">
                        Your meeting link is ready!
                      </p>
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => window.open(consultation.meeting_link, '_blank')}
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Join Meeting
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  )}

                  {consultation.status === "completed" && !consultation.reviewed && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-purple-800 mb-2 font-medium">
                        How was your consultation?
                      </p>
                      <Button 
                        variant="default" 
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          setSelectedConsultation(consultation);
                          setIsReviewModalOpen(true);
                        }}
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Rate & Review
                      </Button>
                    </div>
                  )}

                  {consultation.reviewed && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        ✓ Thank you for your feedback!
                      </p>
                    </div>
                  )}

                  {consultation.payment_status === "pending" && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        Payment is being processed. You'll receive a confirmation email shortly.
                      </p>
                    </div>
                  )}

                  {consultation.status === "pending" && consultation.payment_status === "paid" && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        Payment confirmed! Our counselor will reach out to you 24 hours before the session.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedConsultation && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => {
            setIsReviewModalOpen(false);
            setSelectedConsultation(null);
          }}
          consultation={selectedConsultation}
          onReviewSubmitted={() => {
            fetchConsultations();
          }}
        />
      )}
      <Footer />
    </div>
  );
};

export default MyConsultations;
